import { useCreateContactNote } from '@/hooks/actions/use-create-contact-note';
import { useDeleteContactNote } from '@/hooks/actions/use-delete-contact-note';
import { useUpdateContactNote } from '@/hooks/actions/use-update-contact-note';
import { useContactNotesQuery } from '@/hooks/queries/use-contact-notes-query';
import { useAuth } from '@/contexts/auth-context';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { getInitials } from '@/lib/get-initials';
import type { ContactNoteDto } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import type { ContactNote } from '../contact-types';
import { useResolvedContactIdentityId } from '../use-resolved-contact-identity-id';

export interface ActiveContactNotes {
  notes: ContactNote[];
  isLoading: boolean;
  addNote: (text: string) => void;
  isAdding: boolean;
  updateNote: (id: string, text: string) => void;
  deleteNote: (id: string) => void;
}

const EMPTY_FALLBACK: ActiveContactNotes = {
  notes: [],
  isLoading: false,
  addNote: () => {},
  isAdding: false,
  updateNote: () => {},
  deleteNote: () => {},
};

/** Map a DTO onto the display shape `NoteCard` already renders. */
const toContactNote = (
  dto: ContactNoteDto,
  locale: string,
  authorFallback: string
): ContactNote => {
  const author = dto.authorName.trim() || authorFallback;

  return {
    id: dto.id,
    initials: getInitials(author),
    author,
    date: formatRelativeTime(dto.createdAt, locale),
    text: dto.body,
  };
};

/**
 * Resolves the active notes slice for the NOTES tab.
 *
 * Reads the real, brand-scoped notes list (lazy, keyed by the resolved
 * `creatorIdentityId`) and exposes an optimistic `addNote`. When the identity is
 * unresolved, returns an empty slice so the view shows its shared empty state.
 *
 * DTOs are mapped onto the display `ContactNote` shape: a two-letter monogram and
 * a localized relative date are derived here so `NoteCard` stays unchanged.
 */
export const useActiveContactNotes = (): ActiveContactNotes => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const creatorIdentityId = useResolvedContactIdentityId();

  const authorFallback = t('contactDrawer.notes.removedMember');
  const currentUserName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    t('contactDrawer.notes.you');

  const { data, isLoading } = useContactNotesQuery(creatorIdentityId);

  const { mutate, isPending } = useCreateContactNote({
    creatorIdentityId,
    optimisticAuthorName: currentUserName,
  });
  const { mutate: updateMutate } = useUpdateContactNote(creatorIdentityId);
  const { mutate: deleteMutate } = useDeleteContactNote(creatorIdentityId);

  if (!creatorIdentityId) {
    return EMPTY_FALLBACK;
  }

  return {
    notes: (data ?? []).map((dto) => toContactNote(dto, i18n.language, authorFallback)),
    isLoading,
    addNote: (text: string) => mutate(text),
    isAdding: isPending,
    updateNote: (id: string, text: string) => updateMutate({ noteId: id, body: text }),
    deleteNote: (id: string) => deleteMutate(id),
  };
};
